import React, { useState } from "react";
import { db, storage } from "../firebase";
import { collection, addDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";
import FormContainer from "./styled/FormContainer";
import Form from "./styled/Form";
import FormTitle from "./styled/FormTitle";
import PersonalInfoForm from "./PersonalInfoForm";
import BirthdateForm from "./BirthdateForm";
import DocumentForm from "./DocumentForm";
import CertificateForm from "./CertificateForm";
import FormSubmitButton from "./FormSubmitButton";
import { images } from "../constants";
import { countries } from "./Countries";
import CVForm from "./CVForm";
import useDocumentHandler from "../hooks/useDocumentHandler";
import "react-toastify/dist/ReactToastify.css";
import ToastError from "./styled/ToastError";
import { ValidateFields } from "./validateFields";
import FormLabelDescriptionTitle from "./styled/FormLabelDescriptionTitle";

const Register = () => {
  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [gender, setGender] = useState("");
  const [size, setSize] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("República Dominicana");
  const [birthdate, setBirthdate] = useState(null);
  const [showCedulaInput, setShowCedulaInput] = useState(false);
  const [cedula, setCedula] = useState("");

  const [loader, setLoader] = useState(false);
  const [cvDocument, setCVDocument] = useState(null);
  const [loadingCV, setLoadingCV] = useState(false);

  const {
    documents,
    certificates,
    loading,
    loadingCertificate,
    handleFileChange,
    handleDocumentDelete,
    handleCertificateChange,
    handleCertificateDelete,
    setDocuments,
    setCertificates,
  } = useDocumentHandler();

  const handleCVChange = (file, type) => {
    setLoadingCV(true);
    if (type === "cv") {
      if (file === null) {
        setCVDocument(null);
        setLoadingCV(false);
      } else {
        setTimeout(() => {
          setCVDocument(file);
          setLoadingCV(false);
        }, 2000);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMessage = ValidateFields(
      name,
      lastname,
      phone,
      email,
      confirmEmail,
      birthdate,
      address,
      city,
      showCedulaInput,
      cedula,
      documents,
      cvDocument,
      gender,
      size
    );
    if (errorMessage) {
      ToastError(errorMessage);
      return;
    }

    try {
      setLoader(true);

      const [firstName] = name.split(" ");
      const [firstLastname] = lastname.split(" ");

      const lowerFirstName = firstName.toLowerCase();
      const lowerFirstLastname = firstLastname.toLowerCase();

      // Generar timestamp del registro
      const generateRegistrationTimestamp = () => {
        const now = new Date();

        // Formatear fecha dd-mm-yyyy
        const day = String(now.getDate()).padStart(2, "0");
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const year = now.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;

        // Formatear hora en formato 12 horas con AM/PM
        let hours = now.getHours();
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12; // La hora '0' debe ser '12'
        const formattedTime = `${String(hours).padStart(
          2,
          "0"
        )}:${minutes}:${seconds} ${ampm}`;

        return {
          date: formattedDate,
          time: formattedTime,
          fullTimestamp: `${formattedDate} ${formattedTime}`,
        };
      };

      const registrationTimestamp = generateRegistrationTimestamp();

      const registerData = {
        name: name,
        lastname: lastname,
        gender: gender,
        size: size,
        phone: phone,
        email: email,
        address: address,
        city: city,
        country: country,
        birthdate: birthdate ? birthdate.toLocaleDateString("es-DO") : null,
        cedula: cedula,
        registrationDate: registrationTimestamp.date,
        registrationTime: registrationTimestamp.time,
        registrationTimestamp: registrationTimestamp.fullTimestamp,
      };

      const docRef = await addDoc(collection(db, "register"), registerData);

      const documentURLs = [];
      if (showCedulaInput) {
        for (let i = 0; i < documents.length; i++) {
          const documentRef = ref(
            storage,
            `cedulas/${lowerFirstName}-${lowerFirstLastname}-${cedula}-${i + 1}`
          );
          await uploadBytes(documentRef, documents[i]);
          const downloadURL = await getDownloadURL(documentRef);
          documentURLs.push(downloadURL);
        }
      }

      let curriculumURL = null;
      if (cvDocument) {
        const cvRef = ref(
          storage,
          `curriculums/${lowerFirstName}-${lowerFirstLastname}-${cedula}-1`
        );
        await uploadBytes(cvRef, cvDocument);
        curriculumURL = await getDownloadURL(cvRef);
      }

      const certificateURLs = [];
      if (certificates.length > 0) {
        for (let i = 0; i < certificates.length; i++) {
          const certificateRef = ref(
            storage,
            `certificados/${lowerFirstName}-${lowerFirstLastname}-${cedula}-${
              i + 1
            }`
          );
          await uploadBytes(certificateRef, certificates[i]);
          const downloadURL = await getDownloadURL(certificateRef);
          certificateURLs.push(downloadURL);
        }
      }

      await updateDoc(docRef, {
        documentURLs: documentURLs,
        curriculumURL: curriculumURL,
        certificateURLs: certificateURLs,
      });

      setLoader(false);
      Swal.fire({
        icon: "success",
        title: "¡Registrado exitosamente!",
        showConfirmButton: true,
        confirmButtonColor: "#3085d6",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.message,
        showConfirmButton: true,
        confirmButtonColor: "#d33",
      });
      setLoader(false);
    }

    setName("");
    setLastname("");
    setGender("");
    setSize("");
    setPhone("");
    setEmail("");
    setConfirmEmail("");
    setAddress("");
    setCity("");
    setBirthdate(null);
    setCedula("");
    setShowCedulaInput(false);
    setDocuments([]);
    setCVDocument(null);
    setCertificates([]);
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = today.getMonth() - birthDateObj.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
    ) {
      age--;
    }
    return age;
  };

  const handleBirthdateChange = (date) => {
    setBirthdate(date);

    if (date) {
      setShowCedulaInput(calculateAge(date) >= 18);
    } else {
      setShowCedulaInput(false);
    }
  };

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <img src={images.Banner} alt="Banner" className="banner" />
        <FormTitle>DATATHON 2025</FormTitle>
        <FormLabelDescriptionTitle>
          Edición Universitarios y Profesionales
        </FormLabelDescriptionTitle>
        <PersonalInfoForm
          name={name}
          setName={setName}
          lastname={lastname}
          setLastname={setLastname}
          gender={gender}
          setGender={setGender}
          size={size}
          setSize={setSize}
          phone={phone}
          setPhone={setPhone}
          email={email}
          setEmail={setEmail}
          confirmEmail={confirmEmail}
          setConfirmEmail={setConfirmEmail}
          address={address}
          setAddress={setAddress}
          city={city}
          setCity={setCity}
          country={country}
          setCountry={setCountry}
          countries={countries}
        />

        <BirthdateForm
          birthdate={birthdate}
          handleBirthdateChange={handleBirthdateChange}
          showCedulaInput={showCedulaInput}
          cedula={cedula}
          setCedula={setCedula}
        />

        {showCedulaInput && (
          <DocumentForm
            documents={documents}
            handleDocumentDelete={handleDocumentDelete}
            handleFileChange={(file) => handleFileChange(file, "cedula")}
            loading={loading}
          />
        )}

        <CVForm
          cvDocument={cvDocument}
          handleCVChange={(file) => handleCVChange(file, "cv")}
          loadingCV={loadingCV}
        />

        <CertificateForm
          certificates={certificates}
          handleCertificateChange={handleCertificateChange}
          handleCertificateDelete={handleCertificateDelete}
          loadingCertificate={loadingCertificate}
        />

        <FormSubmitButton loader={loader} />
      </Form>
    </FormContainer>
  );
};

export default Register;
